import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { cn } from "~/lib/utils";
import { Mention, type MentionProps, type SuggestionRenderProps } from "./Mention";

type BlockType = "skill" | "mention" | "tag";

interface TextBlock {
  type: "text" | BlockType;
  content: string;
}

interface MentionConfig {
  trigger: string;
  type: string;
  pattern: RegExp;
  className: string;
  data?: unknown[] | ((query: string, callback: (results: unknown[]) => void) => void | unknown[]);
  displayTransform: (item: unknown) => string;
  appendSpaceOnAdd: boolean;
  renderSuggestions: (props: SuggestionRenderProps) => React.ReactNode;
}

export interface MentionData {
  type: string;
  value: string;
  index: number;
  plainTextIndex: number;
}

interface MentionInputProps {
  value: string;
  onChange: (value: string, mentions?: MentionData[]) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  maxHeight?: number;
  className?: string;
  ariaLabel?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  // Receives the files a paste carried when the clipboard held no text. Absent
  // means every paste is treated as text.
  onPasteFiles?: (files: File[]) => void;
  children?: React.ReactNode;
}

export interface MentionInputRef {
  focus: () => void;
  focusAtEnd: () => void;
  setCursorPosition: (position: number) => void;
}

const ZERO_WIDTH_SPACE = "\u200B";
// Inline token styling for committed mentions, in nextgen design tokens.
const DEFAULT_BLOCK_CLASSNAME =
  "mx-0.5 inline-block rounded bg-primary/10 px-1 py-0 font-medium text-primary";

/**
 * Self-contained contentEditable input with mention/command support.
 *
 * Inspired by react-mentions but using contentEditable instead of a
 * textarea+overlay. Mention behavior is declared via <Mention> children; the
 * parent only ever sees the plain-text value (mentions round-trip as their
 * literal trigger token, e.g. "/kaplan-meier"). Committed mentions render as
 * atomic, non-editable inline blocks that delete as a single unit.
 *
 * Cursor positions are tracked in visible-character space; zero-width spaces
 * pad text nodes around blocks so the caret moves smoothly across boundaries.
 */
export const MentionInput = forwardRef<MentionInputRef, MentionInputProps>((props, ref) => {
  const {
    value,
    onChange,
    onKeyDown,
    onFocus,
    onBlur,
    placeholder,
    maxHeight = 120,
    className,
    ariaLabel = "Message",
    autoFocus,
    disabled,
    onPasteFiles,
    children,
  } = props;

  const editorRef = useRef<HTMLDivElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const isComposingRef = useRef(false);
  // Starts `null` so a non-empty initial `value` still renders on mount.
  const lastValueRef = useRef<string | null>(null);
  const queryIdRef = useRef(0);

  const [suggestionState, setSuggestionState] = useState<{
    isOpen: boolean;
    mentionIndex: number | null;
    position: { top: number; left: number } | null;
    searchText: string;
    queryStart: number;
    queryEnd: number;
    suggestions: unknown[];
  }>({
    isOpen: false,
    mentionIndex: null,
    position: null,
    searchText: "",
    queryStart: 0,
    queryEnd: 0,
    suggestions: [],
  });

  // Reads mention configuration off the <Mention> children.
  const readMentionConfigs = useCallback((): MentionConfig[] => {
    const configs: MentionConfig[] = [];

    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child) && child.type === Mention) {
        const mentionProps = child.props as MentionProps;
        const { trigger, type, pattern, className: blockClassName, data } = mentionProps;

        configs.push({
          trigger,
          type,
          pattern: pattern || derivePatternFromTrigger(trigger),
          className: blockClassName || DEFAULT_BLOCK_CLASSNAME,
          data,
          renderSuggestions: mentionProps.renderSuggestions,
          displayTransform:
            mentionProps.displayTransform ||
            ((item) => {
              const record = item as Record<string, string>;
              return record.display || record.name || record.id;
            }),
          appendSpaceOnAdd: mentionProps.appendSpaceOnAdd !== false,
        });
      }
    });

    return configs;
  }, [children]);

  // "/" → /\/[\w-]+/g, matching the trigger plus word chars and hyphens.
  const derivePatternFromTrigger = (trigger: string): RegExp => {
    const escaped = trigger.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return new RegExp(`${escaped}[\\w-]+`, "g");
  };

  // Triggers only fire at the start of the text or after whitespace.
  const isValidTriggerPosition = useCallback((text: string, index: number): boolean => {
    return index === 0 || /\s/.test(text[index - 1]);
  }, []);

  const moveCursorToEnd = useCallback(() => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    if (!selection) return;

    const range = document.createRange();
    range.selectNodeContents(editorRef.current);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  }, []);

  const saveCursorPosition = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;

    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(editorRef.current!);
    preCaretRange.setEnd(range.endContainer, range.endOffset);

    const text = preCaretRange.toString();
    return text.replace(/\u200B/g, "").length;
  }, []);

  const restoreCursorPosition = useCallback((position: number) => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    if (!selection) return;

    let currentPos = 0;
    let targetNode: Node | null = null;
    let targetOffset = 0;

    const walker = document.createTreeWalker(editorRef.current, NodeFilter.SHOW_TEXT, null);

    while (walker.nextNode()) {
      const node = walker.currentNode;
      const text = node.textContent || "";

      const visibleText = text.replace(/\u200B/g, "");
      const visibleLength = visibleText.length;

      if (currentPos + visibleLength >= position) {
        targetNode = node;

        const visibleOffset = position - currentPos;
        let actualOffset = 0;
        let visibleCount = 0;

        for (let i = 0; i < text.length && visibleCount < visibleOffset; i++) {
          if (text[i] !== "\u200B") {
            visibleCount++;
          }
          actualOffset = i + 1;
        }

        targetOffset = actualOffset;
        break;
      }

      currentPos += visibleLength;
    }

    if (targetNode) {
      const range = document.createRange();
      range.setStart(targetNode, Math.min(targetOffset, targetNode.textContent?.length || 0));
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        editorRef.current?.focus();
      },
      focusAtEnd: () => {
        editorRef.current?.focus();
        setTimeout(() => {
          moveCursorToEnd();
        }, 0);
      },
      setCursorPosition: (position: number) => {
        editorRef.current?.focus();
        setTimeout(() => {
          restoreCursorPosition(position);
        }, 0);
      },
    }),
    [moveCursorToEnd, restoreCursorPosition]
  );

  useEffect(() => {
    if (autoFocus) {
      requestAnimationFrame(() => {
        editorRef.current?.focus();
        moveCursorToEnd();
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reads the plain text out of the DOM, treating block spans as their literal
  // token and stripping the zero-width padding around text nodes.
  const extractPlainText = useCallback((): string => {
    if (!editorRef.current) return "";

    let text = "";
    const walker = document.createTreeWalker(
      editorRef.current,
      NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
      null
    );

    let node;
    while ((node = walker.nextNode())) {
      if (node.nodeType === Node.TEXT_NODE) {
        const parent = node.parentElement;
        if (parent?.hasAttribute("data-block-type")) {
          continue;
        }
        const content = node.textContent || "";
        text += content.replace(/\u200B/g, "");
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        if (element.hasAttribute("data-block-type")) {
          text += element.textContent || "";
        }
      }
    }

    return text;
  }, []);

  const extractMentions = useCallback(
    (text: string, configs: MentionConfig[]): MentionData[] => {
      const mentions: MentionData[] = [];

      configs.forEach((config) => {
        const matches = Array.from(text.matchAll(new RegExp(config.pattern.source, "g")));
        matches.forEach((match) => {
          const index = match.index!;

          if (isValidTriggerPosition(text, index)) {
            mentions.push({
              type: config.type,
              value: match[0],
              index,
              plainTextIndex: index,
            });
          }
        });
      });

      return mentions.sort((a, b) => a.index - b.index);
    },
    [isValidTriggerPosition]
  );

  // Splits the plain-text value into alternating text and mention blocks.
  const parseValue = useCallback(
    (text: string, configs: MentionConfig[]): TextBlock[] => {
      interface Match {
        index: number;
        text: string;
        blockType: string;
      }

      const allMatches: Match[] = [];

      configs.forEach((config) => {
        const matches = Array.from(text.matchAll(new RegExp(config.pattern.source, "g")));
        matches.forEach((match) => {
          const matchIndex = match.index!;
          const matchText = match[0];

          if (isValidTriggerPosition(text, matchIndex)) {
            allMatches.push({
              index: matchIndex,
              text: matchText,
              blockType: config.type,
            });
          }
        });
      });

      allMatches.sort((a, b) => a.index - b.index);

      const blocks: TextBlock[] = [];
      let lastIndex = 0;

      allMatches.forEach((match) => {
        if (match.index > lastIndex) {
          blocks.push({ type: "text", content: text.slice(lastIndex, match.index) });
        }

        blocks.push({ type: match.blockType as BlockType, content: match.text });

        lastIndex = match.index + match.text.length;
      });

      if (lastIndex < text.length) {
        blocks.push({ type: "text", content: text.slice(lastIndex) });
      }

      return blocks.length > 0 ? blocks : text ? [{ type: "text", content: text }] : [];
    },
    [isValidTriggerPosition]
  );

  const clearSuggestions = useCallback(() => {
    queryIdRef.current++;
    setSuggestionState({
      isOpen: false,
      mentionIndex: null,
      position: null,
      searchText: "",
      queryStart: 0,
      queryEnd: 0,
      suggestions: [],
    });
  }, []);

  // Dismiss the open suggestion dropdown on a click outside the editor and the
  // dropdown itself (Escape is handled in the keydown handler). Clicks inside
  // portaled overlays the dropdown spawns — e.g. a skill's actions menu — are
  // left alone so selecting from them still works.
  useEffect(() => {
    if (!suggestionState.isOpen) return;
    const handlePointerDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (editorRef.current?.contains(target)) return;
      if (suggestionsRef.current?.contains(target)) return;
      if (target.closest("[data-radix-popper-content-wrapper]")) return;
      clearSuggestions();
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [suggestionState.isOpen, clearSuggestions]);

  const queryData = useCallback(
    (
      mentionIndex: number,
      query: string,
      queryStart: number,
      queryEnd: number,
      position: { top: number; left: number },
      config: MentionConfig,
      queryId: number
    ) => {
      const { data } = config;

      if (!data) {
        clearSuggestions();
        return;
      }

      if (Array.isArray(data)) {
        const filtered = data.filter((item) =>
          config.displayTransform(item).toLowerCase().includes(query.toLowerCase())
        );

        setSuggestionState({
          isOpen: true,
          mentionIndex,
          position,
          searchText: query,
          queryStart,
          queryEnd,
          suggestions: filtered,
        });
      } else if (typeof data === "function") {
        const result = data(query, (results) => {
          if (queryId === queryIdRef.current) {
            setSuggestionState({
              isOpen: true,
              mentionIndex,
              position,
              searchText: query,
              queryStart,
              queryEnd,
              suggestions: results,
            });
          }
        });

        if (Array.isArray(result)) {
          setSuggestionState({
            isOpen: true,
            mentionIndex,
            position,
            searchText: query,
            queryStart,
            queryEnd,
            suggestions: result,
          });
        }
      }
    },
    [clearSuggestions]
  );

  const insertMention = useCallback(
    (mentionText: string) => {
      const { mentionIndex, queryStart, queryEnd } = suggestionState;
      if (mentionIndex === null) return;

      const configs = readMentionConfigs();
      const config = configs[mentionIndex];

      const insert = config.appendSpaceOnAdd ? `${mentionText} ` : mentionText;

      const newValue = value.substring(0, queryStart) + insert + value.substring(queryEnd);
      const newCursorPos = queryStart + insert.length;

      const mentions = extractMentions(newValue, configs);
      onChange(newValue, mentions);

      clearSuggestions();
      setTimeout(() => {
        restoreCursorPosition(newCursorPos);
      }, 0);
    },
    [
      suggestionState,
      value,
      onChange,
      readMentionConfigs,
      extractMentions,
      clearSuggestions,
      restoreCursorPosition,
    ]
  );

  const renderActiveSuggestions = useCallback((): React.ReactNode => {
    const { mentionIndex, suggestions, searchText, position } = suggestionState;

    if (mentionIndex === null || !position) return null;

    const configs = readMentionConfigs();
    const config = configs[mentionIndex];

    return config.renderSuggestions({
      suggestions,
      searchText,
      position,
      onSelect: insertMention,
      onClose: clearSuggestions,
    });
  }, [suggestionState, readMentionConfigs, insertMention, clearSuggestions]);

  const handleInput = useCallback(() => {
    if (isComposingRef.current) return;

    const plainText = extractPlainText();

    if (plainText !== lastValueRef.current) {
      lastValueRef.current = plainText;
      const configs = readMentionConfigs();
      const mentions = extractMentions(plainText, configs);
      onChange(plainText, mentions);
    }

    if (editorRef.current) {
      const editor = editorRef.current;
      editor.style.height = "auto";
      editor.style.height = `${Math.min(editor.scrollHeight, maxHeight)}px`;
    }

    // Trigger detection works at the DOM level so blocks are skipped cleanly.
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const container = range.startContainer;
      const parentElement =
        container.nodeType === Node.TEXT_NODE
          ? container.parentElement
          : (container as HTMLElement);

      if (parentElement?.hasAttribute?.("data-block-type")) {
        clearSuggestions();
        return;
      }

      if (range.collapsed && range.startOffset === 0 && container.previousSibling) {
        const prev = container.previousSibling;
        if ((prev as HTMLElement).hasAttribute?.("data-block-type")) {
          clearSuggestions();
          return;
        }
      }

      if (container.nodeType === Node.TEXT_NODE) {
        const textBeforeCursor = container.textContent?.slice(0, range.startOffset) || "";
        const configs = readMentionConfigs();
        const queryId = ++queryIdRef.current;

        for (let i = 0; i < configs.length; i++) {
          const config = configs[i];
          const { trigger } = config;

          const lastTriggerIndex = textBeforeCursor.lastIndexOf(trigger);

          if (
            lastTriggerIndex !== -1 &&
            isValidTriggerPosition(textBeforeCursor, lastTriggerIndex)
          ) {
            const searchText = textBeforeCursor.slice(lastTriggerIndex + trigger.length);

            if (!/\s/.test(searchText)) {
              const triggerRange = document.createRange();
              triggerRange.setStart(container, lastTriggerIndex);
              triggerRange.setEnd(container, lastTriggerIndex + trigger.length);
              const rect = triggerRange.getBoundingClientRect();
              const position = { top: rect.top, left: rect.left };

              const cursorPosition = saveCursorPosition();
              if (cursorPosition !== null) {
                const queryStart = cursorPosition - searchText.length - trigger.length;
                queryData(i, searchText, queryStart, cursorPosition, position, config, queryId);
                return;
              }
            }
          }
        }

        clearSuggestions();
      } else {
        clearSuggestions();
      }
    }
  }, [
    extractPlainText,
    onChange,
    maxHeight,
    saveCursorPosition,
    readMentionConfigs,
    extractMentions,
    queryData,
    clearSuggestions,
    isValidTriggerPosition,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;
      if (isComposingRef.current) return;

      // While the dropdown is open, let Enter/Tab bubble to the suggestion
      // list's window listener (which commits the highlighted item) and stop
      // the parent's submit handler from firing. We deliberately don't
      // preventDefault here — that would swallow the event before the list.
      if (suggestionState.isOpen && (e.key === "Enter" || e.key === "Tab")) {
        return;
      }

      if (suggestionState.isOpen && e.key === "Escape") {
        e.preventDefault();
        clearSuggestions();
        return;
      }

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);

      // Backspace immediately after a block deletes the whole block.
      if (e.key === "Backspace" && range.collapsed) {
        const blocks = editorRef.current?.querySelectorAll("[data-block-type]") || [];

        for (let i = blocks.length - 1; i >= 0; i--) {
          const block = blocks[i];
          const blockRange = document.createRange();
          blockRange.selectNode(block);

          const comparison = blockRange.compareBoundaryPoints(Range.END_TO_START, range);

          if (comparison < 0) {
            const betweenRange = document.createRange();
            betweenRange.setStartAfter(block);
            betweenRange.setEnd(range.startContainer, range.startOffset);

            const actualText = betweenRange.toString().replace(/\u200B/g, "");

            if (actualText.length === 0) {
              e.preventDefault();

              const cursorPos = saveCursorPosition();
              const blockLength = block.textContent?.length || 0;

              block.remove();
              handleInput();

              if (cursorPos !== null) {
                setTimeout(() => {
                  restoreCursorPosition(cursorPos - blockLength);
                }, 0);
              }

              return;
            }

            break;
          }
        }
      }

      // Delete with the caret just before a block removes that block.
      if (e.key === "Delete" && range.collapsed) {
        const container = range.startContainer;
        const offset = range.startOffset;

        if (container.nodeType === Node.TEXT_NODE) {
          const textLength = container.textContent?.length || 0;
          if (offset === textLength) {
            const nextNode = container.nextSibling;
            if (nextNode && (nextNode as HTMLElement).hasAttribute?.("data-block-type")) {
              e.preventDefault();
              nextNode.remove();
              handleInput();
              return;
            }
          }
        }
      }

      if (onKeyDown) {
        onKeyDown(e);
      }
    },
    [
      disabled,
      suggestionState,
      clearSuggestions,
      handleInput,
      onKeyDown,
      saveCursorPosition,
      restoreCursorPosition,
    ]
  );

  // Re-render the DOM from the value whenever the parent pushes a value that
  // doesn't already match what the user typed (e.g. programmatic insert or
  // clear-on-submit).
  useEffect(() => {
    if (!editorRef.current || isComposingRef.current) {
      return;
    }

    const plainText = extractPlainText();

    if (value === "") {
      if (editorRef.current.innerHTML !== "") {
        editorRef.current.innerHTML = "";
      }
      editorRef.current.style.height = "auto";
      clearSuggestions();
      lastValueRef.current = value;
      return;
    }

    if (value !== plainText && value !== lastValueRef.current) {
      const configs = readMentionConfigs();
      const blocks = parseValue(value, configs);

      editorRef.current.innerHTML = "";

      if (blocks.length === 0) {
        lastValueRef.current = value;
        return;
      }

      blocks.forEach((block, index) => {
        if (block.type === "text") {
          const textNode = document.createTextNode(
            (index === 0 ? "" : ZERO_WIDTH_SPACE) +
              block.content +
              (index === blocks.length - 1 ? "" : ZERO_WIDTH_SPACE)
          );
          editorRef.current!.appendChild(textNode);
        } else {
          const config = configs.find((c) => c.type === block.type);
          const span = document.createElement("span");
          span.className = config?.className || DEFAULT_BLOCK_CLASSNAME;
          span.contentEditable = "false";
          span.textContent = block.content;
          span.setAttribute("data-block-type", block.type);
          editorRef.current!.appendChild(span);
        }
      });

      moveCursorToEnd();
      lastValueRef.current = value;

      editorRef.current.style.height = "auto";
      editorRef.current.style.height = `${Math.min(editorRef.current.scrollHeight, maxHeight)}px`;
    }
  }, [
    value,
    parseValue,
    extractPlainText,
    moveCursorToEnd,
    maxHeight,
    readMentionConfigs,
    clearSuggestions,
  ]);

  return (
    <>
      <div
        ref={editorRef}
        contentEditable={!disabled}
        onPaste={(e) => {
          if (disabled) return;
          e.preventDefault();
          const text = e.clipboardData.getData("text/plain");
          // Files claim the paste only when no text came with them: a rich copy
          // from a web page bundles its images with the text, and there the text
          // is what the user meant to paste.
          if (onPasteFiles && !text && e.clipboardData.files.length > 0) {
            onPasteFiles(Array.from(e.clipboardData.files));
            return;
          }
          document.execCommand("insertText", false, text);
        }}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        onCompositionStart={() => {
          isComposingRef.current = true;
        }}
        onCompositionEnd={() => {
          isComposingRef.current = false;
          handleInput();
        }}
        className={cn(
          "min-h-6 resize-none overflow-y-auto whitespace-pre-wrap break-words bg-transparent text-left text-sm text-foreground outline-none",
          "empty:before:pointer-events-none empty:before:text-muted-foreground/50 empty:before:content-[attr(data-placeholder)]",
          disabled && "cursor-not-allowed opacity-50",
          className
        )}
        style={{ maxHeight: `${maxHeight}px` }}
        data-placeholder={placeholder}
        role="textbox"
        aria-multiline="true"
        aria-disabled={disabled || undefined}
        aria-label={ariaLabel}
        data-testid="nextgen-composer-input"
        suppressContentEditableWarning
      />
      {suggestionState.isOpen && <div ref={suggestionsRef}>{renderActiveSuggestions()}</div>}
    </>
  );
});

MentionInput.displayName = "MentionInput";

export { Mention } from "./Mention";
